import React, { useState, useEffect } from 'react';
import ChildSwitcher from './ChildSwitcher';
import { useMyChildren } from '../hooks/useMyChildren';

export default function ReportCard() {
  const { children, selectedChild, setSelectedChild, loading } = useMyChildren();
  const [selectedExam, setSelectedExam] = useState('');

  const grades = selectedChild?.grades_by_subject || [];
  const exams = [...new Set(grades.map(g => g.exam).filter(Boolean))].sort();

  useEffect(() => {
    if (exams.length > 0) setSelectedExam(exams[exams.length - 1]);
  }, [selectedChild?.student_id, exams.length]);

  if (loading) {
    return <div className="bg-white p-8 rounded shadow text-center text-gray-500">Loading…</div>;
  }

  if (children.length === 0) {
    return (
      <div className="bg-white p-8 rounded shadow text-center text-gray-400">
        <i className="fas fa-inbox text-4xl mb-3"></i>
        <p>No children linked to your account.</p>
      </div>
    );
  }

  const examGrades = grades.filter(g => g.exam === selectedExam);

  const totalMarks = examGrades.reduce((s, g) => s + (g.total || 100), 0);
  const totalObtained = examGrades.reduce((s, g) => s + (g.marks || 0), 0);
  const percent = totalMarks > 0 ? ((totalObtained / totalMarks) * 100).toFixed(1) : '0.0';
  const overallGrade =
    percent >= 90 ? 'A+' : percent >= 85 ? 'A' : percent >= 80 ? 'A-' :
    percent >= 75 ? 'B+' : percent >= 70 ? 'B' : percent >= 65 ? 'B-' :
    percent >= 60 ? 'C' : percent >= 50 ? 'D' : 'F';

  const handlePrint = () => window.print();

  return (
    <div>
      {/* Page heading — hidden on print */}
      <h2 className="text-xl font-bold mb-4 text-gray-800 no-print">
        <i className="fas fa-file-alt text-blue-500 mr-2"></i>Report Card
      </h2>

      {/* Child switcher — hidden on print */}
      <div className="no-print">
        <ChildSwitcher
          children={children}
          selectedChild={selectedChild}
          onSelect={setSelectedChild}
        />
      </div>

      {exams.length === 0 ? (
        <div className="bg-white p-8 rounded shadow text-center text-gray-400">
          <i className="fas fa-file-alt text-4xl mb-3"></i>
          <p>No grades recorded yet for this student.</p>
        </div>
      ) : (
        <>
          {/* Controls — hidden on print */}
          <div className="bg-white p-3 sm:p-4 rounded shadow mb-4 flex flex-wrap gap-3 items-center no-print">
            <label className="text-sm font-medium text-gray-700">Exam:</label>
            <select
              value={selectedExam}
              onChange={e => setSelectedExam(e.target.value)}
              className="p-2 border rounded text-sm flex-1 min-w-[180px]"
            >
              {exams.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
            >
              <i className="fas fa-print mr-1"></i> Print
            </button>
          </div>

          {/* Report card — this is what prints */}
          <div id="report-card" className="print-area bg-white p-6 sm:p-8 rounded shadow">
            <div className="text-center border-b pb-4 mb-6">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800">Report Card</h3>
              <p className="text-sm text-gray-500 mt-1">{selectedExam}</p>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm mb-6">
              <div>
                <p className="text-xs text-gray-500">Student Name</p>
                <p className="font-semibold">{selectedChild?.name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Class</p>
                <p className="font-semibold">{selectedChild?.class_name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Registration #</p>
                <p className="font-mono text-xs">{selectedChild?.registration_number || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Campus</p>
                <p className="font-semibold">{selectedChild?.campus_name}</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="p-2 text-left border">Subject</th>
                    <th className="p-2 text-right border">Marks</th>
                    <th className="p-2 text-right border">Total</th>
                    <th className="p-2 text-right border">%</th>
                    <th className="p-2 text-center border">Grade</th>
                  </tr>
                </thead>
                <tbody>
                  {examGrades.map((g, i) => (
                    <tr key={i}>
                      <td className="p-2 border">{g.subject}</td>
                      <td className="p-2 text-right border">{g.marks}</td>
                      <td className="p-2 text-right border">{g.total}</td>
                      <td className="p-2 text-right border">{g.percentage}%</td>
                      <td className="p-2 text-center border font-semibold">{g.grade}</td>
                    </tr>
                  ))}
                  <tr className="bg-blue-50 font-bold">
                    <td className="p-2 border">Total</td>
                    <td className="p-2 text-right border">{totalObtained}</td>
                    <td className="p-2 text-right border">{totalMarks}</td>
                    <td className="p-2 text-right border">{percent}%</td>
                    <td className="p-2 text-center border">{overallGrade}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-8 pt-6 border-t">
              <div className="text-center">
                <div className="border-t border-gray-400 w-40 mx-auto mb-2"></div>
                <p className="text-xs text-gray-500">Class Teacher</p>
              </div>
              <div className="text-center">
                <div className="border-t border-gray-400 w-40 mx-auto mb-2"></div>
                <p className="text-xs text-gray-500">Principal</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}